import { useQuery } from "@tanstack/react-query";
import { Link, useMatchRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";
import { formatCount } from "../lib/format.ts";
import { meQuery, usageQuery } from "../lib/queries.ts";
import styles from "./AppHeader.module.css";
import { GooglyEyes } from "./GooglyEyes.tsx";
import { Icon } from "./Icon.tsx";
import ui from "./ui.module.css";

const SHORTCUT = navigator.userAgent.includes("Mac") ? "⌘K" : "Ctrl K";

export function AppHeader() {
  const me = useQuery(meQuery);
  const email = me.data?.email;
  const matchRoute = useMatchRoute();
  const inGroups = Boolean(matchRoute({ to: "/groups", fuzzy: true }));
  const inTrash = Boolean(matchRoute({ to: "/trash" }));
  const inSearch = Boolean(matchRoute({ to: "/search" }));
  const inUsage = Boolean(matchRoute({ to: "/usage" }));
  const inActivity = Boolean(matchRoute({ to: "/activity" }));
  const inTokens = Boolean(matchRoute({ to: "/tokens" }));
  const inFiles = !inGroups && !inTrash && !inSearch && !inUsage && !inActivity && !inTokens;
  const menu = useRef<HTMLDivElement>(null);
  const closeMenu = () => menu.current?.hidePopover();

  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        <Link to="/" className={styles.brand}>
          <GooglyEyes follow className={styles.mark} />
          <span className={styles.wordmark}>Vault</span>
        </Link>
        <SearchBox />
        <Link
          to="/search"
          search={{ q: "" }}
          className={`${ui.iconButton} ${styles.searchLink}`}
          data-variant="frame"
          aria-label="Search files"
          title="Search files"
        >
          <Icon name="search" />
        </Link>
        <nav className={styles.nav} aria-label="Sections">
          <Link to="/" className={styles.navItem} aria-current={inFiles ? "page" : undefined}>
            <Icon name="files" />
            <span className={styles.navLabel}>Files</span>
          </Link>
          <Link
            to="/groups"
            className={styles.navItem}
            aria-current={inGroups ? "page" : undefined}
          >
            <Icon name="people" />
            <span className={styles.navLabel}>Groups</span>
          </Link>
          <Link to="/trash" className={styles.navItem} aria-current={inTrash ? "page" : undefined}>
            <Icon name="trash" />
            <span className={styles.navLabel}>Trash</span>
          </Link>
          <UsageNavItem current={inUsage} />
          <Link
            to="/activity"
            className={`${styles.navItem} ${styles.navActivity}`}
            aria-current={inActivity ? "page" : undefined}
          >
            <Icon name="pulse" />
            <span className={styles.navLabel}>Activity</span>
          </Link>
        </nav>
        {email ? (
          <div className={styles.account}>
            <button
              type="button"
              className={styles.accountButton}
              popoverTarget="account-menu"
              aria-label={`Account: ${email}`}
            >
              <span className={styles.avatar} aria-hidden="true">
                {email.charAt(0).toUpperCase()}
              </span>
              <span className={styles.email}>{email}</span>
            </button>
            <div ref={menu} id="account-menu" popover="auto" className={styles.menu}>
              <p className={styles.menuLabel}>Signed in as</p>
              <p className={styles.menuEmail}>{email}</p>
              <Link
                to="/activity"
                className={`${styles.menuItem} ${styles.menuActivity}`}
                onClick={closeMenu}
              >
                <Icon name="pulse" />
                Activity
              </Link>
              <Link to="/tokens" className={styles.menuItem} onClick={closeMenu}>
                <Icon name="key" />
                API tokens
              </Link>
              <a
                href="/api/docs"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.menuItem}
                onClick={closeMenu}
              >
                <Icon name="external" />
                API reference
              </a>
              <a href="/auth/logout" className={styles.menuItem}>
                <Icon name="signOut" />
                Sign out
              </a>
            </div>
          </div>
        ) : (
          <span className={styles.accountPlaceholder} aria-hidden="true" />
        )}
      </div>
    </header>
  );
}

function UsageNavItem({ current }: { current: boolean }) {
  const usage = useQuery(usageQuery);
  const alerts = usage.data?.alerts ?? [];
  const level = alerts.some((alert) => alert.level === "critical")
    ? "critical"
    : alerts.length > 0
      ? "warn"
      : undefined;

  return (
    <Link to="/usage" className={styles.navItem} aria-current={current ? "page" : undefined}>
      <span className={styles.navGlyph}>
        <Icon name="gauge" />
        {level ? <span className={styles.badge} data-level={level} /> : null}
      </span>
      <span className={styles.navLabel}>Usage</span>
      {level ? (
        <span className={ui.visuallyHidden}>{formatCount(alerts.length, "alert", "alerts")}</span>
      ) : null}
    </Link>
  );
}

function SearchBox() {
  const navigate = useNavigate();
  const field = useRef<HTMLInputElement>(null);
  const fieldId = useId();
  const term = useRouterState({
    select: (state) =>
      state.location.pathname === "/search"
        ? ((state.location.search as { q?: string }).q ?? "")
        : "",
  });
  const [draft, setDraft] = useState(term);

  useEffect(() => setDraft(term), [term]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      const input = field.current;
      // The field is hidden on narrow headers; the search page carries its own.
      if (input?.checkVisibility()) {
        input.focus();
        input.select();
      } else {
        void navigate({ to: "/search", search: { q: "" } });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <form
      className={styles.searchForm}
      onSubmit={(event) => {
        event.preventDefault();
        void navigate({ to: "/search", search: { q: draft.trim() } });
      }}
    >
      <label htmlFor={fieldId} className={ui.visuallyHidden}>
        Search files
      </label>
      <Icon name="search" className={styles.searchIcon} />
      <input
        ref={field}
        id={fieldId}
        type="search"
        className={styles.searchInput}
        value={draft}
        placeholder="Search files"
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") event.currentTarget.blur();
        }}
      />
      <span className={styles.shortcut} aria-hidden="true">
        {SHORTCUT}
      </span>
    </form>
  );
}
